import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Plus, ExternalLink, Eye, EyeOff, Clock } from "lucide-react";
import { adminListPosts, adminDeletePost } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListPosts();
      setPosts(data);
    } catch (e) {
      toast.error("Couldn't load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminDeletePost(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.title}".`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const published = posts.filter((p) => p.published).length;
  const drafts = posts.length - published;

  return (
    <div data-testid="admin-posts-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline text-terracotta">Blog manager</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink mt-2">
            Your posts
          </h1>
          <p className="text-sm text-ink-muted mt-2">
            {posts.length} total · {published} published · {drafts} drafts
          </p>
        </div>
        <Link
          to="/admin/new"
          data-testid="admin-new-post-link"
          className="inline-flex items-center gap-2 bg-forest text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-forest-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> New post
        </Link>
      </div>

      {loading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-paper-100 border border-paper-200 rounded-sm animate-pulse" />
          ))}
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="bg-paper-100 border border-paper-200 p-10 rounded-sm text-center">
          <p className="text-ink-muted">No posts yet. Click "New post" to write your first.</p>
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div className="space-y-3" data-testid="admin-posts-list">
          {posts.map((p) => (
            <article
              key={p.id}
              data-testid={`admin-post-row-${p.id}`}
              className="bg-paper-100 border border-paper-200 p-5 rounded-sm flex flex-col sm:flex-row sm:items-center gap-4"
            >
              {p.cover_image && (
                <img
                  src={p.cover_image}
                  alt=""
                  className="w-20 h-20 object-cover rounded-sm shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {p.published ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-forest text-paper-50">
                      <Eye className="w-3 h-3" /> Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-paper-200 text-ink">
                      <EyeOff className="w-3 h-3" /> Draft
                    </span>
                  )}
                  <span className="text-[11px] uppercase tracking-wider text-terracotta font-bold">
                    {p.category}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-ink-muted">
                    <Clock className="w-3 h-3" /> {p.read_minutes} min · {p.published_at}
                  </span>
                </div>
                <h2 className="font-display font-bold text-lg sm:text-xl text-ink mt-1.5 leading-snug truncate">
                  {p.title}
                </h2>
                <p className="text-sm text-ink-muted mt-1 line-clamp-2">{p.excerpt}</p>
              </div>
              <div className="flex items-center gap-2 sm:flex-col sm:items-end shrink-0">
                {p.published && (
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-post-view-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-forest"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View
                  </a>
                )}
                <Link
                  to={`/admin/edit/${p.id}`}
                  data-testid={`admin-post-edit-${p.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-paper-50 border border-paper-200 rounded-sm hover:bg-forest hover:text-paper-50 hover:border-forest transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Link>
                <button
                  onClick={() => setDeleteTarget(p)}
                  data-testid={`admin-post-delete-${p.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-paper-50 border border-paper-200 rounded-sm hover:bg-terracotta hover:text-paper-50 hover:border-terracotta transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="admin-delete-confirm"
              className="bg-terracotta hover:bg-terracotta-dark text-paper-50"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
